import { useRouter } from "next/router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { setLayoutProps } from "../../../services/redux/slices/LayoutSlice";
import { HeaderEnum, PagesEnum } from "../../../utils/enums";
import { isArabicLanguage } from "../../../utils/help/localization/localization";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import styles from "./OurPoliciesScreen.module.scss";

type PolicyTab = "terms" | "refund" | "shipping" | "privacy" | "contact";

// URL-hash deep links (e.g. /our-policies#privacy-policy) so external links —
// like the Google Play listing's privacy policy URL — land on the right tab
// instead of always opening Terms & Conditions.
const HASH_TO_TAB: Record<string, PolicyTab> = {
  "terms-and-conditions": "terms",
  terms: "terms",
  "refund-policy": "refund",
  refund: "refund",
  "delivery-policy": "shipping",
  delivery: "shipping",
  shipping: "shipping",
  "privacy-policy": "privacy",
  privacy: "privacy",
  "contact-info": "contact",
  contact: "contact",
};

export const OurPoliciesScreen = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslationI18();
  const [activeTab, setActiveTab] = useState<PolicyTab>("terms");
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left?: number;
    right?: number;
    width: number;
  }>({ left: 0, width: 0 });
  const [backgroundWidth, setBackgroundWidth] = useState("100%");
  const tabsRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  const mainLayoutData: any = useAppSelector(
    (state) => state.layout.layoutProps
  );

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  const activeFranchises = useAppSelector(
    (state) => state.config.activeFranchises
  );

  // Get localized restaurant name based on current language
  const currentLanguage = router.locale || i18n.language || "en";
  const restaurantName =
    (currentLanguage !== "en" &&
      (restaurantInfo as any)?.nameLocalized &&
      (restaurantInfo as any).nameLocalized) ||
    restaurantInfo?.name ||
    "";

  // Google Play requires the privacy policy linked from a listing to identify
  // the app. Restaurants whose backend config sets `hasAppPrivacyPolicy` get an
  // "About This App and Its Developer" section on the Privacy Policy tab. It is
  // not `hasMobileApp`, because that flag also opens the app banner, which is
  // hard-wired to a single app. The app name is the restaurant's base
  // (non-localized) name, which is what the store listing carries.
  const showAppIdentification = Boolean(restaurantConfig?.hasAppPrivacyPolicy);
  const appName = restaurantInfo?.name || "";

  useEffect(() => {
    const applyHashTab = () => {
      const hash = window.location.hash.replace("#", "").toLowerCase();
      const tab = HASH_TO_TAB[hash];
      if (tab) {
        setActiveTab(tab);
      }
    };

    applyHashTab();
    window.addEventListener("hashchange", applyHashTab);

    return () => {
      window.removeEventListener("hashchange", applyHashTab);
    };
  }, []);

  useLayoutEffect(() => {
    const headerComponents: HeaderEnum[] = [HeaderEnum.Back];

    const headerProps = {
      restaurantInfo: null,
      components: headerComponents,
      backLink: getPageLink(router, PagesEnum.Home).as,
      currentPage: PagesEnum.OurPolicies,
      dynamicPagination: null,
      display: true,
    };
    dispatch(
      setLayoutProps({
        ...(mainLayoutData || {}),
        headerProps,
        footerProps: {
          display: false,
        },
      })
    );
  }, []);

  const tabs = [
    { id: "terms" as PolicyTab, label: t("our-policies:tabs.terms") },
    { id: "refund" as PolicyTab, label: t("our-policies:tabs.refund") },
    { id: "shipping" as PolicyTab, label: t("our-policies:tabs.delivery") },
    { id: "privacy" as PolicyTab, label: t("our-policies:tabs.privacy") },
    { id: "contact" as PolicyTab, label: t("our-policies:tabs.contact") },
  ];

  const updateIndicatorPosition = () => {
    if (activeTabRef.current && tabsRef.current) {
      const activeTabRect = activeTabRef.current.getBoundingClientRect();
      const tabsRect = tabsRef.current.getBoundingClientRect();

      const isRTL = isArabicLanguage();
      let left: number | undefined;
      let right: number | undefined;

      if (isRTL) {
        // For RTL languages, use right property instead of left
        // Calculate distance from container's right edge to tab's right edge
        right = tabsRect.right - activeTabRect.right;
        left = undefined;
      } else {
        // For LTR languages, calculate position from the left
        left = activeTabRect.left - tabsRect.left;
        right = undefined;
      }

      const width = activeTabRect.width;

      setIndicatorStyle({ left, right, width });
      setBackgroundWidth(`${tabsRef.current.scrollWidth}px`);
    }
  };

  useEffect(() => {
    updateIndicatorPosition();

    const handleResize = () => {
      updateIndicatorPosition();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [activeTab]);

  const renderContent = (
    tab: PolicyTab,
    restaurantName: string,
    franchises: any[]
  ) => {
    switch (tab) {
      case "terms":
        return (
          <div className={styles.policy_content}>
            <p>
              {t("our-policies:terms.intro", {
                restaurant_name: restaurantName,
              })}
            </p>
            <h3>{t("our-policies:terms.acceptance_title")}</h3>
            <p>{t("our-policies:terms.acceptance_text")}</p>
            <h3>{t("our-policies:terms.use_title")}</h3>
            <ul>
              <li>{t("our-policies:terms.use_item1")}</li>
              <li>{t("our-policies:terms.use_item2")}</li>
              <li>{t("our-policies:terms.use_item3")}</li>
            </ul>
            <h3>{t("our-policies:terms.orders_title")}</h3>
            <ul>
              <li>{t("our-policies:terms.orders_item1")}</li>
              <li>{t("our-policies:terms.orders_item2")}</li>
              <li>{t("our-policies:terms.orders_item3")}</li>
            </ul>
            <h3>{t("our-policies:terms.liability_title")}</h3>
            <p>
              {t("our-policies:terms.liability_text", {
                restaurant_name: restaurantName,
              })}
            </p>
            <h3>{t("our-policies:terms.modifications_title")}</h3>
            <p>{t("our-policies:terms.modifications_text")}</p>
          </div>
        );

      case "privacy":
        return (
          <div className={styles.policy_content} id="privacy-policy">
            <p>
              {t("our-policies:privacy.intro", {
                restaurant_name: restaurantName,
              })}
            </p>
            <h3>{t("our-policies:privacy.collect_title")}</h3>
            <ul>
              <li>{t("our-policies:privacy.collect_item1")}</li>
              <li>{t("our-policies:privacy.collect_item2")}</li>
              <li>{t("our-policies:privacy.collect_item3")}</li>
            </ul>
            <h3>{t("our-policies:privacy.use_title")}</h3>
            <ul>
              <li>{t("our-policies:privacy.use_item1")}</li>
              <li>{t("our-policies:privacy.use_item2")}</li>
              <li>{t("our-policies:privacy.use_item3")}</li>
            </ul>
            <h3>{t("our-policies:privacy.sharing_title")}</h3>
            <ul>
              <li>{t("our-policies:privacy.sharing_item1")}</li>
              <li>{t("our-policies:privacy.sharing_item2")}</li>
              <li>{t("our-policies:privacy.sharing_item3")}</li>
            </ul>
            <h3>{t("our-policies:privacy.rights_title")}</h3>
            <ul>
              <li>{t("our-policies:privacy.rights_item1")}</li>
              <li>{t("our-policies:privacy.rights_item2")}</li>
            </ul>
            <h3>{t("our-policies:privacy.security_title")}</h3>
            <p>{t("our-policies:privacy.security_text")}</p>
            <h3>{t("our-policies:privacy.changes_title")}</h3>
            <p>{t("our-policies:privacy.changes_text")}</p>
            <h3>{t("our-policies:privacy.bot_protection_title")}</h3>
            <p>
              {t("our-policies:privacy.bot_protection_text")}{" "}
              <a
                href="https://www.cloudflare.com/en-gb/turnstile-privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("our-policies:privacy.bot_protection_link")}
              </a>
              .
            </p>
            {showAppIdentification && (
              <>
                <h3>{t("our-policies:privacy.about_app_title")}</h3>
                <p>
                  {t("our-policies:privacy.about_app_applies", {
                    app_name: appName,
                  })}
                </p>
                <p>{t("our-policies:privacy.about_app_owner")}</p>
                <p>{t("our-policies:privacy.about_app_developer")}</p>
                <p>{t("our-policies:privacy.about_app_platform")}</p>
                <p>{t("our-policies:privacy.about_app_contact_intro")}</p>
                <ul>
                  <li>{t("our-policies:privacy.about_app_contact_owner")}</li>
                  <li>
                    {t("our-policies:privacy.about_app_contact_developer")}
                  </li>
                </ul>
              </>
            )}
          </div>
        );

      case "refund":
        return (
          <div className={styles.policy_content}>
            <p>
              {t("our-policies:refund.intro", {
                restaurant_name: restaurantName,
              })}
            </p>
            <h3>{t("our-policies:refund.eligibility_title")}</h3>
            <ul>
              <li>{t("our-policies:refund.eligibility_item1")}</li>
              <li>{t("our-policies:refund.eligibility_item2")}</li>
              <li>{t("our-policies:refund.eligibility_item3")}</li>
            </ul>
            <h3>{t("our-policies:refund.process_title")}</h3>
            <ul>
              <li>{t("our-policies:refund.process_item1")}</li>
              <li>{t("our-policies:refund.process_item2")}</li>
            </ul>
            <h3>{t("our-policies:refund.exceptions_title")}</h3>
            <ul>
              <li>{t("our-policies:refund.exceptions_item1")}</li>
              <li>{t("our-policies:refund.exceptions_item2")}</li>
            </ul>
            <p>{t("our-policies:refund.closing")}</p>
          </div>
        );

      case "shipping":
        return (
          <div className={styles.policy_content}>
            <p>
              {t("our-policies:delivery.intro", {
                restaurant_name: restaurantName,
              })}
            </p>
            <h3>{t("our-policies:delivery.timing_title")}</h3>
            <ul>
              <li>{t("our-policies:delivery.timing_item1")}</li>
              <li>{t("our-policies:delivery.timing_item2")}</li>
            </ul>
            <h3>{t("our-policies:delivery.areas_title")}</h3>
            <ul>
              <li>{t("our-policies:delivery.areas_item1")}</li>
            </ul>
            <h3>{t("our-policies:delivery.accuracy_title")}</h3>
            <ul>
              <li>{t("our-policies:delivery.accuracy_item1")}</li>
              <li>{t("our-policies:delivery.accuracy_item2")}</li>
            </ul>
            <h3>{t("our-policies:delivery.fees_title")}</h3>
            <p>{t("our-policies:delivery.fees_text")}</p>
          </div>
        );

      case "contact":
        return (
          <div className={styles.policy_content}>
            {franchises && franchises.length > 0 ? (
              <ul className={styles.franchise_list}>
                {franchises.map((franchise) => (
                  <li key={franchise.id} className={styles.franchise_item}>
                    <p className={styles.franchise_name}>{franchise.name}</p>
                    {franchise.phoneNumber && (
                      <p className={styles.franchise_phone}>
                        {franchise.phoneNumber}
                      </p>
                    )}
                    {franchise.address && (
                      <p className={styles.franchise_address}>
                        {franchise.address}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>{t("our-policies:contact.no_franchises")}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t("our-policies:title")}</h1>

      <div className={styles.tabs_container}>
        <div ref={tabsRef} className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              ref={activeTab === tab.id ? activeTabRef : null}
              className={`${styles.tab} ${
                activeTab === tab.id ? styles.tab_active : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className={styles.tab_indicator}>
          <div
            className={styles.tab_indicator_background}
            style={{
              width: backgroundWidth,
              ...(isArabicLanguage()
                ? { right: 0, left: "auto" }
                : { left: 0, right: "auto" }),
            }}
          />
          <div
            className={styles.tab_indicator_line}
            style={{
              ...(indicatorStyle.left !== undefined
                ? { left: `${indicatorStyle.left}px` }
                : {}),
              ...(indicatorStyle.right !== undefined
                ? { right: `${indicatorStyle.right}px` }
                : {}),
              width: `${indicatorStyle.width}px`,
            }}
          />
        </div>
      </div>

      <div className={styles.content}>
        {renderContent(activeTab, restaurantName, activeFranchises)}
      </div>
    </div>
  );
};
